import type { TFunction } from 'i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import type { CandidateScoringStatus } from '@/models/job';
import { Tooltip } from '@/components/ui/tooltip';
import { Tag } from '@/components/ui/atoms';
import type { TagIntent } from '@/components/ui/atoms/Tag/Tag.types';
import { cn } from '@/utils/cn';

const STATUS_LABEL_KEYS = {
  stale: 'job:table.scoring_status.stale',
  rescoring: 'job:table.scoring_status.rescoring',
  failed: 'job:table.scoring_status.failed',
  quota_blocked: 'job:table.scoring_status.quota_blocked',
} as const satisfies Record<CandidateScoringStatus, string>;

interface MatchScoreTagProps {
  scoreLabel: string;
  intent: TagIntent;
  status?: CandidateScoringStatus;
  t: TFunction<['job', 'candidates', 'common']>;
}

/** Match % Tag; optional rescoring/failed glyph via Tag `icon` (inside the pill). */
export const MatchScoreTag = ({ scoreLabel, intent, status, t }: MatchScoreTagProps) => {
  const inProgress = status === 'stale' || status === 'rescoring';
  const StatusIcon = inProgress ? RefreshCw : AlertCircle;
  const statusLabel = status ? t(STATUS_LABEL_KEYS[status]) : undefined;
  const accessibleName = statusLabel ? `${scoreLabel}. ${statusLabel}` : undefined;

  const tag = (
    <Tag
      intent={intent}
      variant="subtle"
      aria-label={accessibleName}
      icon={
        status ? (
          <StatusIcon
            className={cn(inProgress && 'animate-spin [animation-duration:2s]')}
            aria-hidden
          />
        ) : undefined
      }
    >
      {scoreLabel}
    </Tag>
  );

  if (!statusLabel) return tag;

  return (
    <Tooltip content={statusLabel} variant="top">
      {tag}
    </Tooltip>
  );
};
